import React, { useState } from 'react';
import { useRouter } from 'next/router';
import CustomLink from '@common/CustomLink';
import SvgCaret from '@common/Icons/Caret';
import { NavigationItem } from '@common/Providers/GraphqlProvider/generated';
import { AnimatePresence, motion } from 'framer-motion';

import SvgHouse from '../../../common/Icons/House';
import { useLayoutContext } from '../../../common/Layout/LayoutProvider/LayoutContext';

type MobileMenuItemProps = {
  item: NavigationItem;
  depth: number;
  closeMenu: () => void;
};

const MobileMenuItem: React.FC<MobileMenuItemProps> = ({ item, depth, closeMenu }) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasChildren = item.children && item.children.length > 0;

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  const handleClick = (e: React.MouseEvent) => {
    if (hasChildren) {
      e.preventDefault();
      setIsOpen(!isOpen);
    } else {
      closeMenu();
    }
  };

  return (
    <div className="w-full">
      <div
        className={`flex items-center justify-between py-3 ${
          depth > 0 ? `pl-${depth * 4} pr-4` : 'px-4'
        } relative`}
      >
        {depth > 0 && (
          <div
            className="absolute bottom-0 left-0 top-0 w-px bg-primary"
            style={{ left: `${depth * 16 - 8}px` }}
          />
        )}
        {!hasChildren ? (
          <CustomLink
            href={item.linkItem?.url || '/'}
            pageLink={item.linkItem}
            onClick={closeMenu}
            className="flex-grow text-left"
          >
            <span className={`text-sm font-medium ${depth > 0 ? 'text-gray-600' : ''}`}>
              {item.name}
            </span>
          </CustomLink>
        ) : (
          <div className="flex-grow text-left" onClick={handleClick}>
            <span className={`text-sm font-medium ${depth > 0 ? 'text-gray-600' : ''}`}>
              {item.name}
            </span>
          </div>
        )}
        {hasChildren && (
          <button onClick={handleToggle} className="p-2">
            <SvgCaret
              className={`h-4 w-4 transform transition-transform ${isOpen ? 'rotate-180' : ''}`}
            />
          </button>
        )}
      </div>
      {hasChildren && (
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3 }}
              className={depth > 0 ? 'ml-4 border-l border-gray-200' : ''}
            >
              {item.children?.map((child, index) => (
                <MobileMenuItem key={index} item={child} depth={depth + 1} closeMenu={closeMenu} />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      )}
    </div>
  );
};

type MobileCollapsibleMenuProps = {
  isOpen: boolean;
  onClose: () => void;
  headerHeight?: string;
};

const MobileCollapsibleMenu: React.FC<MobileCollapsibleMenuProps> = ({
  isOpen,
  onClose,
  headerHeight = '52px',
}) => {
  const {
    layout: { navigationItems = [] },
  } = useLayoutContext();

  const homeItem = {
    name: 'Inicio',
    link: '/',
  };
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-x-0 bottom-0 z-40 bg-white"
          style={{ top: headerHeight }}
        >
          <div className="flex h-full flex-col overflow-y-auto">
            <nav className="flex-grow">
              <div className="flex items-center px-4 py-3">
                <SvgHouse className="mr-2 h-5 w-5 text-primary" />
                <CustomLink href="/" className="flex-grow" onClick={onClose}>
                  <span className="text-sm font-medium">{homeItem.name}</span>
                </CustomLink>
              </div>
              {navigationItems.map((item, index) => (
                <MobileMenuItem key={index} item={item} depth={0} closeMenu={onClose} />
              ))}
            </nav>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MobileCollapsibleMenu;
